import {
  SecondaryButton,
  Link,
  AccordionTable,
  Table,
} from "@sentara-health/optimahealth_web";
import { t } from "i18next";
import {
  BrokerAPIStatus,
  PastDueBalanceSummaryItem,
} from "@sentara-health/sentara-api-hooks-core";
import { convertToDate } from "utils/helpers/DateFormate";
import { useNavigate } from "react-router";
import { UNPAID_GROUP_BALANCE } from "utils/constants/Routes";
import { amountFormat } from "utils/helpers/HelperFn";
import { useGlobalState, useBranding, Brands } from "providers";
import { DeviceTypes } from "utils/model/CommonTypes";
import { GroupStatus } from "utils/constants/Constants";
import { downloadXLSX } from "utils/helpers/DownloadFiles";

interface UnpaidGroupBalanceTableProps {
  // groupSummary: Array<EmployerGroupSummaryResponse>;
  pastDueSummary: Array<PastDueBalanceSummaryItem>;
  spanDays?: string | null;
  status: string;
  setIsSubmited: (v: boolean) => void;
}
interface GroupStatusLocal {
  A: string;
  I: string;
}

export const UnpaidBalanceNotes = () => {
  return (
    <div className="d-flex flex-column gap-3" data-testid="unpaidnotes">
      <div>
        <strong data-testid="txtNotesLabel">
          {t("unpaidGroupBalance.content.notes")}:
        </strong>{" "}
        <span data-testid="txtNotesInDetail">
          {t("unpaidGroupBalance.content.descText3")}
        </span>
      </div>
      <div>{t("unpaidGroupBalance.content.descText4")}</div>
    </div>
  );
};
const UnpaidGroupBalanceTable = ({
  pastDueSummary,
  spanDays,
  status,
  setIsSubmited,
}: UnpaidGroupBalanceTableProps) => {
  const navigate = useNavigate();
  const { deviceType } = useGlobalState();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const { brandConfig } = useBranding();

  const header = [
    {
      text:
        brandConfig.brandName === Brands.AVMED
          ? t("unpaidGroupBalance.content.accountNumber")
          : t("unpaidGroupBalance.content.groupNumber"),
    },
    {
      text:
        brandConfig.brandName === Brands.AVMED
          ? t("unpaidGroupBalance.content.accountName")
          : t("unpaidGroupBalance.content.groupName"),
    },
    { text: t("unpaidGroupBalance.content.totalDue") },
    { text: t("unpaidGroupBalance.content.oldestInvoice") },
    { text: t("unpaidGroupBalance.content.agentName") },
    {
      text:
        brandConfig.brandName === Brands.AVMED
          ? t("unpaidGroupBalance.content.accountStatus")
          : t("unpaidGroupBalance.content.groupStatus"),
    },
  ];

  const handleDownload = (jsonData: any) => {
    const customJson = jsonData.map((item: PastDueBalanceSummaryItem) => {
      let status;

      //add new object response based on branding equal to avmed
      if (brandConfig.brandName === Brands.AVMED) {
        status = GroupStatus[item.accountStatus as keyof typeof GroupStatus];

        return {
          "Account Number": item.accountId,
          "Account Name": item.accountName,
          "Total Due": amountFormat(item.amountDue),
          "Oldest Invoice with no Payment": convertToDate(item.invoiceDate),
          "Agent Name": item.agentName,
          "Account Status": t(status),
        };
      } else {
        if (item.groupStatus == "A") {
          status = GroupStatus["A"];
        } else {
          status = GroupStatus["I"];
        }

        return {
          "Group Number": item.groupID,
          "Group Name": item.groupName,
          "Total Due": amountFormat(item.amountDue),
          "Oldest Invoice with no Payment": convertToDate(item.invoiceDate),
          "Agent Name": item.agentName,
          "Group Status": t(status),
        };
      }
    });
    downloadXLSX(customJson);
  };

  const getUnpaidBalanceDetailsUrl = (item: PastDueBalanceSummaryItem) => {
    if (brandConfig.brandName === Brands.AVMED) {
      const searchParams = new URLSearchParams({
        accountId: item.accountId ?? "",
        accountName: item.accountName ?? "",
      });

      return `/app/${UNPAID_GROUP_BALANCE}/${item.accountId}?${searchParams.toString()}`;
    }

    return `/app/${UNPAID_GROUP_BALANCE}/${item.groupID}`;
  };

  const getUnpaidBalanceTableData = () => {
    let tableData: any = [];
    let rowData = [];
    if (pastDueSummary.length) {
      pastDueSummary.map((item, index) => {
        rowData = [];

        rowData.push({
          text: (
            <Link
              data-testid={`viewButton${index}`}
              onClick={() => setIsSubmited(true)}
              to={getUnpaidBalanceDetailsUrl(item)}
              className="darkblue-color bg-transparent border-0 text-decoration-underline p-0"
            >
              {brandConfig.brandName === Brands.AVMED
                ? item.accountId
                : item.groupID}
            </Link>
          ),
        });

        rowData.push({
          text:
            brandConfig.brandName === Brands.AVMED
              ? item.accountName
              : item.groupName,
        });
        rowData.push({ text: amountFormat(item.amountDue) });
        rowData.push({ text: convertToDate(item.invoiceDate) });
        rowData.push({ text: item.agentName });
        rowData.push({
          text:
            brandConfig.brandName === Brands.AVMED
              ? t(GroupStatus[item.accountStatus as keyof typeof GroupStatus])
              : t(GroupStatus[item.groupStatus as keyof typeof GroupStatus]),
        });

        tableData.push(rowData);
      });
    } else if (status !== BrokerAPIStatus.PROCESSING) {
      rowData.push({ text: t("noResult") });
      tableData.push(rowData);
    }

    return tableData;
  };

  return (
    <div>
      <div className="border light-grey-bg border-radius10 p-3 mb-4">
        <strong data-testid="txtYouSearchedFor">
          {t("unpaidGroupBalance.content.youSearchedFor")}:
        </strong>{" "}
        {spanDays && (
          <span data-testid="txtYouSearchedForDescription">
            {t("unpaidGroupBalance.content.searchForSpandays")}
            {spanDays} {t("unpaidGroupBalance.content.days")}
          </span>
        )}
      </div>

      <div className="mb-4">
        <SecondaryButton
          data-testid="btnPerformNewSearch"
          className={"secondary_btn btn_block_mob"}
          onClick={() => navigate(`/app/${UNPAID_GROUP_BALANCE}`)}
        >
          {t("button.label.performANewSearch")}
        </SecondaryButton>
      </div>

      <div className="border light-grey-bg border-radius10 p-3 mb-4">
        <div className="pb-2">
          <strong data-testid="txtInstructionsLabel">
            {t("unpaidGroupBalance.content.instructions")}:
          </strong>{" "}
          <span data-testid="txtDetailedInstructions">
            {t("unpaidGroupBalance.content.descText2")}
          </span>
        </div>
        <UnpaidBalanceNotes />
      </div>
      <div className="mb-4">
        <SecondaryButton
          data-testid="btnPerformdownloadGroupAll"
          className={"secondary_btn btn_block_mob"}
          onClick={() => handleDownload(pastDueSummary)}
        >
          {t("button.label.downloadAll")}
        </SecondaryButton>
      </div>
      {!isMobile && (
        <div className="table-responsive mb-4">
          <Table
            data-testid="tblGroupDetails"
            header={header}
            rows={getUnpaidBalanceTableData()}
          />
        </div>
      )}

      {isMobile &&
        (pastDueSummary.length > 0 ? (
          <div className="mb-4">
            <AccordionTable
              initialActiveIndex={0}
              id="unpaid_group_balance_search_table"
              items={
                pastDueSummary.length > 0 &&
                pastDueSummary.map((item, index) => ({
                  title: (
                    <div>
                      <div>
                        {brandConfig.brandName === Brands.AVMED
                          ? t("unpaidGroupBalance.content.accountNumber")
                          : t("unpaidGroupBalance.content.groupNumber")}
                      </div>
                      <div>
                        <Link
                          onClick={() => setIsSubmited(true)}
                          data-testid={`viewButton${index}`}
                          to={getUnpaidBalanceDetailsUrl(item)}
                        >
                          {brandConfig.brandName === Brands.AVMED
                            ? item.accountId
                            : item.groupID}
                        </Link>
                      </div>
                    </div>
                  ),
                  id: "unpaid_group_balance_search_table_data",
                  content: (
                    <>
                      <div className="mb-3">
                        <div>
                          {brandConfig.brandName === Brands.AVMED
                            ? t("unpaidGroupBalance.content.accountName")
                            : t("unpaidGroupBalance.content.groupName")}
                        </div>
                        <div>
                          {brandConfig.brandName === Brands.AVMED
                            ? item.accountName
                            : item.groupName}
                        </div>
                      </div>
                      <div className="mb-3">
                        <div>{t("unpaidGroupBalance.content.totalDue")}</div>
                        <div>{amountFormat(item.amountDue)}</div>
                      </div>
                      <div className="mb-3">
                        <div>
                          {t("unpaidGroupBalance.content.oldestInvoice")}
                        </div>
                        <div>{convertToDate(item.invoiceDate)}</div>
                      </div>
                      <div className="mb-3">
                        <div>
                          {brandConfig.brandName === Brands.AVMED
                            ? t("unpaidGroupBalance.content.accountStatus")
                            : t("unpaidGroupBalance.content.groupStatus")}
                        </div>
                        <div>
                          {brandConfig.brandName === Brands.AVMED
                            ? t(
                                GroupStatus[
                                  item.accountStatus as keyof typeof GroupStatus
                                ],
                              )
                            : t(
                                GroupStatus[
                                  item.groupStatus as keyof typeof GroupStatus
                                ],
                              )}
                        </div>
                      </div>
                    </>
                  ),
                }))
              }
            />
          </div>
        ) : (
          <>
            {status !== BrokerAPIStatus.PROCESSING && (
              <div className="mb-4">
                <div className="text-center">{t("noResult")}</div>
              </div>
            )}
          </>
        ))}
    </div>
  );
};

export default UnpaidGroupBalanceTable;
